import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  Users,
  Settings,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Plus,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Zap,
  Bot,
  CalendarDays,
  CalendarCheck,
  CalendarX,
  Info,
  Sun,
  Moon,
  Timer,
  Copy,
  Palette,
  MessageSquareText,
  Mail,
  AtSign,
  Link,
  Type,
  AlertTriangle
} from 'lucide-react';
import { GOOGLE_CALENDAR_CONFIG, APP_NAME } from '../../../config';

const { ipcRenderer } = window.require('electron');

// Objetos de mapeamento para tempos e durações
const MIN_SCHEDULE_TIME_OPTIONS = {
  '30min': '30 minutos',
  '1h': '1 hora',
  '1h30min': '1 hora e 30 minutos',
  '2h': '2 horas',
  '3h': '3 horas',
  '4h': '4 horas',
  '6h': '6 horas',
  '8h': '8 horas',
  '12h': '12 horas',
  '24h': '24 horas'
};

const MEETING_DURATION_OPTIONS = {
  '15min': '15 minutos',
  '30min': '30 minutos',
  '45min': '45 minutos',
  '60min': '1 hora',
  '90min': '1h 30min',
  '120min': '2 horas',
  '180min': '3 horas',
  '240min': '4 horas'
};

// Configurações completas das opções para os selects
const MIN_SCHEDULE_TIME_CONFIG = [
  { value: '30min', label: '30 minutos', desc: 'Muito rápido' },
  { value: '1h', label: '1 hora', desc: 'Recomendado', isDefault: true },
  { value: '1h30min', label: '1h 30min', desc: 'Moderado' },
  { value: '2h', label: '2 horas', desc: 'Confortável' },
  { value: '3h', label: '3 horas', desc: 'Meio período' },
  { value: '4h', label: '4 horas', desc: 'Meio dia' },
  { value: '6h', label: '6 horas', desc: 'Longo prazo' },
  { value: '8h', label: '8 horas', desc: 'Um dia' },
  { value: '12h', label: '12 horas', desc: 'Meio dia' },
  { value: '24h', label: '24 horas', desc: 'Dia completo' }
];

const MEETING_DURATION_CONFIG = [
  { value: '15min', label: '15 minutos', desc: 'Reunião rápida' },
  { value: '30min', label: '30 minutos', desc: 'Conversa curta' },
  { value: '45min', label: '45 minutos', desc: 'Reunião média' },
  { value: '60min', label: '1 hora', desc: 'Padrão', isDefault: true },
  { value: '90min', label: '1h 30min', desc: 'Reunião longa' },
  { value: '120min', label: '2 horas', desc: 'Workshop' },
  { value: '180min', label: '3 horas', desc: 'Sessão extensa' },
  { value: '240min', label: '4 horas', desc: 'Meio período' }
];

// Configuração dos dias da semana
const WEEKDAYS_CONFIG = [
  { key: 'monday', label: 'Segunda-feira', short: 'SEG' },
  { key: 'tuesday', label: 'Terça-feira', short: 'TER' },
  { key: 'wednesday', label: 'Quarta-feira', short: 'QUA' },
  { key: 'thursday', label: 'Quinta-feira', short: 'QUI' },
  { key: 'friday', label: 'Sexta-feira', short: 'SEX' },
  { key: 'saturday', label: 'Sábado', short: 'SAB' },
  { key: 'sunday', label: 'Domingo', short: 'DOM' }
];
const GoogleCalendarSection = ({ instanceId, settings, onSettingsChange, knowledgeBaseActive }) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    return localStorage.getItem('showGoogleCalendar') === 'true';
  });

  const [calendarStatus, setCalendarStatus] = useState({
    connected: false,
    userEmail: '',
    lastSync: null,
    availableSlots: 0,
    upcomingMeetings: 0,
    authUrl: null,
    isAuthenticating: false
  });

  const [functionCallingEnabled, setFunctionCallingEnabled] = useState(false);

  const [minScheduleTime, setMinScheduleTime] = useState('1h');
  const [isMinScheduleExpanded, setIsMinScheduleExpanded] = useState(false);

  const [meetingDuration, setMeetingDuration] = useState('60min');
  const [isMeetingDurationExpanded, setIsMeetingDurationExpanded] = useState(false);

  const [workingHours, setWorkingHours] = useState({
    monday: { enabled: true, start: '09:00', end: '18:00' },
    tuesday: { enabled: true, start: '09:00', end: '18:00' },
    wednesday: { enabled: true, start: '09:00', end: '18:00' },
    thursday: { enabled: true, start: '09:00', end: '18:00' },
    friday: { enabled: true, start: '09:00', end: '18:00' },
    saturday: { enabled: false, start: '09:00', end: '18:00' },
    sunday: { enabled: false, start: '09:00', end: '18:00' }
  });
  const [isWorkingHoursExpanded, setIsWorkingHoursExpanded] = useState(false);

  const [customMessageEnabled, setCustomMessageEnabled] = useState(false);
  const [emailRequired, setEmailRequired] = useState(true);
  const [successMessage, setSuccessMessage] = useState('*CONVITE PARA REUNIÃO*\n\n📋 *Assunto:* Reunião com {NOME_PARTICIPANTE} - {DATA_FORMATADA} às {HORA}\n📅 *Data:* {DATA_COMPLETA}\n⏰ *Horário:* {HORA}\n\n🔗 *Link do Google Meet:*\n{LINK_REUNIAO}\n\n📧 *Confirmação:* Enviada para: {EMAIL_PARTICIPANTE}\n⏰ *Lembretes:* Você receberá notificações antes da reunião\n\n✅ *Para participar:* Clique no link na hora marcada\n📱 *Pelo celular:* Baixe o app Google Meet\n💻 *Pelo computador:* Acesse direto pelo navegador\n\nNos vemos lá! 😊');
  const [isSuccessMessageExpanded, setIsSuccessMessageExpanded] = useState(false);

  const [customTitleEnabled, setCustomTitleEnabled] = useState(false);
  const [meetingTitle, setMeetingTitle] = useState('Reunião com {NOME_PARTICIPANTE} - {DATA_FORMATADA} às {HORA}');
  const [isMeetingTitleExpanded, setIsMeetingTitleExpanded] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokens, setTokens] = useState({
    access_token: '',
    refresh_token: ''
  });

  const [isLoadingCalendarData, setIsLoadingCalendarData] = useState(false);
  const [lastDataLoad, setLastDataLoad] = useState(null);
  const [lastConnectivityCheck, setLastConnectivityCheck] = useState(null);
  const [connectivityCheckInterval, setConnectivityCheckInterval] = useState(null);

  // Verificar status de autenticação ao inicializar
  useEffect(() => {
    checkAuthStatus();
  }, [instanceId]);

  // Health check para monitorar conectividade contínua
  useEffect(() => {
    if (calendarStatus.connected) {
      const startHealthMonitoring = () => {
        const intervalId = setInterval(async () => {
          await performConnectivityHealthCheck();
        }, 120000); // Check every 2 minutes

        setConnectivityCheckInterval(intervalId);
        return intervalId;
      };

      const intervalId = startHealthMonitoring();

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    } else {
      if (connectivityCheckInterval) {
        clearInterval(connectivityCheckInterval);
        setConnectivityCheckInterval(null);
      }
    }
  }, [calendarStatus.connected]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (connectivityCheckInterval) {
        clearInterval(connectivityCheckInterval);
      }
    };
  }, []);

  const performConnectivityHealthCheck = async () => {
    try {
      const now = Date.now();
      if (lastConnectivityCheck && (now - lastConnectivityCheck) < 60000) {
        return; // Skip if checked less than 1 minute ago
      }

      const result = await ipcRenderer.invoke('google-calendar-test-connectivity', instanceId);
      setLastConnectivityCheck(now);

      if (result.success && !result.isConnected) {
        console.warn('🔥 Google Calendar connectivity lost - updating status');
        setCalendarStatus(prev => ({
          ...prev,
          connected: false,
          userEmail: '',
          lastSync: null,
          availableSlots: 0,
          upcomingMeetings: 0
        }));
      } else if (result.success && result.isConnected && !calendarStatus.connected) {
        console.log('✅ Google Calendar connectivity restored');
        await checkAuthStatus();
      }
    } catch (error) {
      console.error('❌ Health check failed:', error);
    }
  };

  // Sincronizar estado local com as configurações passadas do pai
  useEffect(() => {
    // Só atualiza se o valor realmente mudou para evitar loops
    if (settings && settings.GOOGLE_CALENDAR_FUNCTION_CALLING !== undefined &&
         settings.GOOGLE_CALENDAR_FUNCTION_CALLING !== functionCallingEnabled) {
      setFunctionCallingEnabled(settings.GOOGLE_CALENDAR_FUNCTION_CALLING);
    }
    if (settings && settings.GOOGLE_CALENDAR_MIN_SCHEDULE_TIME !== undefined &&
         settings.GOOGLE_CALENDAR_MIN_SCHEDULE_TIME !== minScheduleTime) {
      setMinScheduleTime(settings.GOOGLE_CALENDAR_MIN_SCHEDULE_TIME);
    }
    if (settings && settings.GOOGLE_CALENDAR_MEETING_DURATION !== undefined &&
         settings.GOOGLE_CALENDAR_MEETING_DURATION !== meetingDuration) {
      setMeetingDuration(settings.GOOGLE_CALENDAR_MEETING_DURATION);
    }
    if (settings && settings.GOOGLE_CALENDAR_WORKING_HOURS !== undefined &&
         JSON.stringify(settings.GOOGLE_CALENDAR_WORKING_HOURS) !== JSON.stringify(workingHours)) {
      setWorkingHours(settings.GOOGLE_CALENDAR_WORKING_HOURS);
    }
    if (settings && settings.GOOGLE_CALENDAR_SUCCESS_MESSAGE !== undefined &&
         settings.GOOGLE_CALENDAR_SUCCESS_MESSAGE !== successMessage) {
      setSuccessMessage(settings.GOOGLE_CALENDAR_SUCCESS_MESSAGE);
    }
        if (settings && settings.GOOGLE_CALENDAR_CUSTOM_MESSAGE_ENABLED !== undefined &&
        settings.GOOGLE_CALENDAR_CUSTOM_MESSAGE_ENABLED !== customMessageEnabled) {
      console.log('🔍 [CALENDAR SYNC DEBUG] Sincronizando toggle do settings:');
      console.log('  - settings.GOOGLE_CALENDAR_CUSTOM_MESSAGE_ENABLED:', settings.GOOGLE_CALENDAR_CUSTOM_MESSAGE_ENABLED);
      console.log('  - typeof:', typeof settings.GOOGLE_CALENDAR_CUSTOM_MESSAGE_ENABLED);
      console.log('  - customMessageEnabled atual:', customMessageEnabled);
      console.log('  - typeof atual:', typeof customMessageEnabled);
      setCustomMessageEnabled(settings.GOOGLE_CALENDAR_CUSTOM_MESSAGE_ENABLED);
      console.log('✅ [CALENDAR SYNC DEBUG] Estado local atualizado');
    }
    // Sincroniza nova configuração GOOGLE_CALENDAR_EMAIL_REQUIRED
    if (settings && settings.GOOGLE_CALENDAR_EMAIL_REQUIRED !== undefined &&
        settings.GOOGLE_CALENDAR_EMAIL_REQUIRED !== emailRequired) {
      console.log('📧 [EMAIL CONFIG SYNC] Sincronizando nova configuração de e-mail obrigatório:');
      console.log('  - settings.GOOGLE_CALENDAR_EMAIL_REQUIRED:', settings.GOOGLE_CALENDAR_EMAIL_REQUIRED);
      console.log('  - typeof:', typeof settings.GOOGLE_CALENDAR_EMAIL_REQUIRED);
      console.log('  - emailRequired atual:', emailRequired);
      setEmailRequired(settings.GOOGLE_CALENDAR_EMAIL_REQUIRED);
      console.log('✅ [EMAIL CONFIG SYNC] Estado local atualizado');
    }

    if (settings && settings.GOOGLE_CALENDAR_CUSTOM_TITLE_ENABLED !== undefined &&
        settings.GOOGLE_CALENDAR_CUSTOM_TITLE_ENABLED !== customTitleEnabled) {
      setCustomTitleEnabled(settings.GOOGLE_CALENDAR_CUSTOM_TITLE_ENABLED);
    }
    if (settings && settings.GOOGLE_CALENDAR_MEETING_TITLE !== undefined &&
        settings.GOOGLE_CALENDAR_MEETING_TITLE !== meetingTitle) {
      setMeetingTitle(settings.GOOGLE_CALENDAR_MEETING_TITLE);
    }

  }, [settings, functionCallingEnabled, minScheduleTime, meetingDuration, workingHours, successMessage, customMessageEnabled, emailRequired, customTitleEnabled, meetingTitle]);

  const toggleFunctionCalling = () => {
    const canUseFunctionCalling =
      settings?.AI_SELECTED === 'GEMINI' ||
      settings?.AI_SELECTED === 'GPT' ||
      settings?.AI_SELECTED === 'PUREROUTER';

    if (!canUseFunctionCalling) {
      return;
    }

    const newValue = !functionCallingEnabled;
    setFunctionCallingEnabled(newValue);

    if (onSettingsChange) {
      onSettingsChange({
        target: {
          name: 'GOOGLE_CALENDAR_FUNCTION_CALLING',
          value: newValue,
          type: 'checkbox',
          checked: newValue
        }
      });
    }
  };

  const handleMinScheduleTimeChange = (newTime) => {
    setMinScheduleTime(newTime);

    if (onSettingsChange) {
      onSettingsChange({
        target: {
          name: 'GOOGLE_CALENDAR_MIN_SCHEDULE_TIME',
          value: newTime,
          type: 'select'
        }
      });
    }
  };

  const handleMeetingDurationChange = (newDuration) => {
    setMeetingDuration(newDuration);

    if (onSettingsChange) {
      onSettingsChange({
        target: {
          name: 'GOOGLE_CALENDAR_MEETING_DURATION',
          value: newDuration,
          type: 'select'
        }
      });
    }
  };

  const handleSuccessMessageChange = (newMessage) => {
    setSuccessMessage(newMessage);

    if (onSettingsChange) {
      onSettingsChange({
        target: {
          name: 'GOOGLE_CALENDAR_SUCCESS_MESSAGE',
          value: newMessage,
          type: 'textarea'
        }
      });
    }
  };

  const toggleCustomMessage = () => {
    const newValue = !customMessageEnabled;
    console.log('🔍 [CALENDAR TOGGLE DEBUG] Toggle mensagem personalizada:');
    console.log('  - Estado atual (customMessageEnabled):', customMessageEnabled);
    console.log('  - Novo valor (newValue):', newValue);
    console.log('  - typeof newValue:', typeof newValue);
    console.log('  - onSettingsChange existe:', !!onSettingsChange);

    setCustomMessageEnabled(newValue);

    if (onSettingsChange) {
      const eventObj = {
        target: {
          name: 'GOOGLE_CALENDAR_CUSTOM_MESSAGE_ENABLED',
          value: newValue,
          type: 'checkbox',
          checked: newValue
        }
      };

      console.log('🔍 [CALENDAR TOGGLE DEBUG] Objeto sendo enviado para onSettingsChange:', eventObj);
      onSettingsChange(eventObj);
      console.log('✅ [CALENDAR TOGGLE DEBUG] onSettingsChange chamado com sucesso');
    } else {
      console.log('❌ [CALENDAR TOGGLE DEBUG] onSettingsChange não existe!');
    }
  };

  const handleMeetingTitleChange = (newTitle) => {
    setMeetingTitle(newTitle);

    if (onSettingsChange) {
      onSettingsChange({
        target: {
          name: 'GOOGLE_CALENDAR_MEETING_TITLE',
          value: newTitle,
          type: 'textarea'
        }
      });
    }
  };

  const toggleCustomTitle = () => {
    const newValue = !customTitleEnabled;
    setCustomTitleEnabled(newValue);

    if (onSettingsChange) {
      onSettingsChange({
        target: {
          name: 'GOOGLE_CALENDAR_CUSTOM_TITLE_ENABLED',
          value: newValue,
          type: 'checkbox',
          checked: newValue
        }
      });
    }
  };

  const toggleEmailRequired = () => {
    const newValue = !emailRequired;
    console.log('📧 [EMAIL CONFIG TOGGLE] Toggle e-mail obrigatório:');
    console.log('  - Estado atual (emailRequired):', emailRequired);
    console.log('  - Novo valor (newValue):', newValue);
    console.log('  - typeof newValue:', typeof newValue);
    console.log('  - onSettingsChange existe:', !!onSettingsChange);

    setEmailRequired(newValue);

    if (onSettingsChange) {
      const eventObj = {
        target: {
          name: 'GOOGLE_CALENDAR_EMAIL_REQUIRED',
          value: newValue,
          type: 'checkbox',
          checked: newValue
        }
      };

      console.log('📧 [EMAIL CONFIG TOGGLE] Objeto sendo enviado para onSettingsChange:', eventObj);
      onSettingsChange(eventObj);
      console.log('✅ [EMAIL CONFIG TOGGLE] onSettingsChange chamado com sucesso');
    } else {
      console.log('❌ [EMAIL CONFIG TOGGLE] onSettingsChange não existe!');
    }
  };

  const handleWorkingHoursChange = (newWorkingHours) => {
    setWorkingHours(newWorkingHours);

    if (onSettingsChange) {
      onSettingsChange({
        target: {
          name: 'GOOGLE_CALENDAR_WORKING_HOURS',
          value: newWorkingHours,
          type: 'object'
        }
      });
    }
  };

  const toggleDayEnabled = (day) => {
    const newWorkingHours = {
      ...workingHours,
      [day]: {
        ...workingHours[day],
        enabled: !workingHours[day].enabled
      }
    };
    handleWorkingHoursChange(newWorkingHours);
  };

  const handleDayTimeChange = (day, timeType, value) => {
    const newWorkingHours = {
      ...workingHours,
      [day]: {
        ...workingHours[day],
        [timeType]: value
      }
    };
    handleWorkingHoursChange(newWorkingHours);
  };

  const applyWorkingHoursPreset = (preset) => {
    let newWorkingHours = { ...workingHours };

    if (preset === 'business') {
      newWorkingHours = {
        monday: { enabled: true, start: '08:00', end: '18:00' },
        tuesday: { enabled: true, start: '08:00', end: '18:00' },
        wednesday: { enabled: true, start: '08:00', end: '18:00' },
        thursday: { enabled: true, start: '08:00', end: '18:00' },
        friday: { enabled: true, start: '08:00', end: '18:00' },
        saturday: { enabled: false, start: '08:00', end: '18:00' },
        sunday: { enabled: false, start: '08:00', end: '18:00' }
      };
    } else {
      return;
    }

    handleWorkingHoursChange(newWorkingHours);
  };

  // Verificar status de autenticação com validação robusta
  const checkAuthStatus = async () => {
    try {
      const connectivityResult = await ipcRenderer.invoke('google-calendar-test-connectivity', instanceId);

      if (connectivityResult.success && connectivityResult.isConnected) {
        setCalendarStatus(prev => ({
          ...prev,
          connected: true,
          userEmail: connectivityResult.userEmail || 'Usuário conectado'
        }));

        await loadCalendarData();
      } else {
        setCalendarStatus(prev => ({
          ...prev,
          connected: false,
          userEmail: '',
          lastSync: null,
          availableSlots: 0,
          upcomingMeetings: 0
        }));

        if (connectivityResult.requiresReauth) {
          console.log('🔄 Authentication required - tokens may be expired');
        }
      }
    } catch (error) {
      console.error('❌ Enhanced auth status check failed:', error);
      setCalendarStatus(prev => ({
        ...prev,
        connected: false,
        userEmail: '',
        lastSync: null,
        availableSlots: 0,
        upcomingMeetings: 0
      }));
    }
  };

  const loadCalendarData = async (forceRefresh = false) => {
    if (isLoadingCalendarData && !forceRefresh) {
      return;
    }

    const now = Date.now();
    if (!forceRefresh && lastDataLoad && (now - lastDataLoad) < 30000) {
      return;
    }

    try {
      setIsLoadingCalendarData(true);

      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0); // Início do dia atual
      const endDate = new Date();
      endDate.setHours(23, 59, 59, 999); // Final do dia atual

      console.log('📅 [LOAD CALENDAR DEBUG] Exibindo dados apenas do dia atual:');
      console.log(`   🕐 Data início: ${startDate.toISOString()}`);
      console.log(`   🕐 Data fim: ${endDate.toISOString()}`);
      console.log(`   📆 Período: ${startDate.toLocaleDateString('pt-BR')} (somente hoje)`);

      const [slotsResult, eventsResult] = await Promise.all([
        ipcRenderer.invoke('google-calendar-get-available-slots', {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          instanceId
        }),
        ipcRenderer.invoke('google-calendar-get-upcoming-events', {
          days: 1, // 🆕 Apenas 1 dia (hoje)
          instanceId
        })
      ]);

      // Debug detalhado dos resultados
      console.log('🔍 [LOAD CALENDAR DATA DEBUG] Resultados das chamadas IPC:');
      console.log('   📅 Slots result:', {
        success: slotsResult.success,
        count: slotsResult.count,
        error: slotsResult.error
      });
      console.log('   📋 Events result:', {
        success: eventsResult.success,
        count: eventsResult.count,
        events: eventsResult.events?.length,
        error: eventsResult.error
      });

      if (eventsResult.success && eventsResult.events) {
        console.log('   📋 Eventos encontrados:', eventsResult.events.map(e => ({
          summary: e.summary || 'Sem título',
          start: e.start?.dateTime || e.start?.date,
          end: e.end?.dateTime || e.end?.date
        })));
      }

      if (slotsResult.requiresReauth || eventsResult.requiresReauth) {
        console.log('⚠️ [AUTH] Reautenticação necessária - atualizando status para desconectado');
        setCalendarStatus(prev => ({
          ...prev,
          connected: false,
          availableSlots: 0,
          upcomingMeetings: 0
        }));
        return;
      }

      setCalendarStatus(prev => ({
        ...prev,
        availableSlots: slotsResult.success ? slotsResult.count : 0,
        upcomingMeetings: eventsResult.success ? eventsResult.count : 0,
        lastSync: new Date()
      }));

      setLastDataLoad(now);

    } catch (error) {
      console.error('Erro ao carregar dados do calendário', error);
    } finally {
      setIsLoadingCalendarData(false);
    }
  };

  const toggleExpanded = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    localStorage.setItem('showGoogleCalendar', newState.toString());
  };

  const handleConnect = async () => {
    try {
      setCalendarStatus(prev => ({ ...prev, isAuthenticating: true }));

      // Gerar URL de autenticação
      const result = await ipcRenderer.invoke('google-calendar-get-auth-url', instanceId);

      if (result.success) {
        setCalendarStatus(prev => ({ ...prev, authUrl: result.authUrl }));
        setShowAuthModal(true);

        // Abrir URL no navegador padrão
        if (window.require) {
          const { shell } = window.require('electron');
          await shell.openExternal(result.authUrl);
        } else {
          window.open(result.authUrl, '_blank');
        }
      } else {
        console.error('Erro ao gerar URL de autenticação', result.error);
        setCalendarStatus(prev => ({ ...prev, isAuthenticating: false }));
      }

    } catch (error) {
      console.error('Erro ao iniciar autenticação', error);
      setCalendarStatus(prev => ({ ...prev, isAuthenticating: false }));
    }
  };

  const handleAuthCode = async () => {
    if (!authCode.trim()) {
      return;
    }

    try {
      setIsLoading(true);

      // Processar código de autorização
      const result = await ipcRenderer.invoke('google-calendar-handle-auth-code', {
        code: authCode,
        instanceId
      });

      if (result.success) {
        setCalendarStatus(prev => ({
          ...prev,
          connected: true,
          userEmail: result.user.email || 'Email não disponível',
          isAuthenticating: false,
          authUrl: null
        }));

        setShowAuthModal(false);
        setAuthCode('');

        // Carregar dados do calendário
        await loadCalendarData();

      } else {
        console.error('Falha na autenticação', result.error);
        alert(`Erro na autenticação: ${result.error}`);
      }

    } catch (error) {
      console.error('Erro durante processamento do código de autorização', error);
      alert(`Erro: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTokensDirectly = async () => {
    if (!tokens.access_token.trim()) {
      alert('Access token é obrigatório');
      return;
    }

    try {
      setIsLoading(true);

      const result = await ipcRenderer.invoke('google-calendar-set-tokens', {
        tokens,
        instanceId
      });

      if (result && result.success) {
        setCalendarStatus(prev => ({
          ...prev,
          connected: true,
          userEmail: result.user?.email || 'Email não disponível',
          isAuthenticating: false,
          authUrl: null
        }));

        setShowTokenModal(false);
        setTokens({ access_token: '', refresh_token: '' });

        await loadCalendarData();

      } else {
        const errorMsg = result?.error || 'Handler não encontrado ou resposta inválida';
        console.error('Falha na configuração de tokens', errorMsg);
        alert(`Erro na configuração: ${errorMsg}`);
      }

    } catch (error) {
      console.error('Erro durante configuração de tokens', error);
      alert(`Erro: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openTokenAuthUrl = () => {
    const authUrl = 'https://accounts.google.com/o/oauth2/auth?' + new URLSearchParams({
      client_id: GOOGLE_CALENDAR_CONFIG.CLIENT_ID,
      redirect_uri: GOOGLE_CALENDAR_CONFIG.REDIRECT_URI,
      response_type: 'code',
      scope: GOOGLE_CALENDAR_CONFIG.SCOPES.join(' '),
      access_type: 'offline',
      prompt: 'consent'
    });

    console.info('Abrindo URL de autorização para tokens', { authUrl });

    if (window.require) {
      const { shell } = window.require('electron');
      shell.openExternal(authUrl);
    } else {
      window.open(authUrl, '_blank');
    }
  };

  const handleDisconnect = async () => {
    try {
      console.info('Iniciando desconexão do Google Calendar...');

      const result = await ipcRenderer.invoke('google-calendar-disconnect', instanceId);
      console.info('Resposta da desconexão', result);

      if (result.success) {
        console.info('Google Calendar desconectado com sucesso');

        setCalendarStatus({
          connected: false,
          userEmail: '',
          lastSync: null,
          availableSlots: 0,
          upcomingMeetings: 0,
          authUrl: null,
          isAuthenticating: false
        });
      } else {
        console.error('Erro ao desconectar', { error: result.error });
        alert(`Erro ao desconectar: ${result.error}`);
      }

    } catch (error) {
      console.error('Erro ao desconectar Google Calendar', {
        error: error.message,
        stack: error.stack
      });
      alert(`Erro: ${error.message}`);
    }
  };

  const handleRefresh = async () => {
    if (!calendarStatus.connected) {
      console.warn('Tentativa de sincronização sem estar conectado');
      return;
    }

    try {
      console.info('Iniciando sincronização manual...');
      setIsLoading(true);

      await loadCalendarData(true);
      console.info('Sincronização manual concluída');

    } catch (error) {
      console.error('Erro durante sincronização manual', {
        error: error.message,
        stack: error.stack
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testConnection = async () => {
    try {
      console.info('Testando conexão...', { instanceId });

      const result = await ipcRenderer.invoke('google-calendar-test-connection', instanceId);

      console.info('Resultado do teste de conexão', result);

      if (result.success) {
        console.info('Teste de conexão bem-sucedido', result);
        alert(`✅ Conexão OK!\nUsuário: ${result.userInfo?.email}\nEventos encontrados: ${result.eventsCount}`);
      } else {
        console.error('Falha no teste de conexão', { error: result.error });
        alert(`❌ Falha na conexão: ${result.error}`);
      }
    } catch (error) {
      console.error('Erro no teste de conexão', {
        error: error.message,
        stack: error.stack
      });
      alert(`❌ Erro: ${error.message}`);
    }
  };

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-grayBase/90 via-slateCard/95 to-grayBase/90 border border-grayMedium/50 rounded-xl shadow-xl">
      {/* Animated background glow - luzes verdes/água embaçadas */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-10 left-20 w-32 h-32 bg-emeraldMedium/20 rounded-full blur-3xl animate-pulse" style={{animationDuration: '4s'}}></div>
        <div className="absolute bottom-20 right-16 w-40 h-40 bg-cyanTertiary/15 rounded-full blur-3xl animate-pulse" style={{animationDuration: '5s', animationDelay: '1s'}}></div>
        <div className="absolute top-32 right-32 w-24 h-24 bg-tealTertiary/25 rounded-full blur-2xl animate-pulse" style={{animationDuration: '3s', animationDelay: '2s'}}></div>
        <div className="absolute bottom-40 left-10 w-36 h-36 bg-emeraldLight/10 rounded-full blur-3xl animate-pulse" style={{animationDuration: '6s', animationDelay: '0.5s'}}></div>
        <div className="absolute top-16 center w-28 h-28 bg-cyanSecondary/20 rounded-full blur-2xl animate-pulse" style={{animationDuration: '4.5s', animationDelay: '3s'}}></div>
      </div>

      {/* Floating moving lights */}
      <div className="absolute inset-0 opacity-40">
        <div className="absolute w-3 h-3 bg-emeraldMedium/60 rounded-full blur-sm animate-bounce" style={{top: '15%', left: '10%', animationDelay: '0s', animationDuration: '3s'}}></div>
        <div className="absolute w-2 h-2 bg-cyanTertiary/70 rounded-full blur-sm animate-bounce" style={{top: '25%', right: '15%', animationDelay: '1s', animationDuration: '4s'}}></div>
        <div className="absolute w-2.5 h-2.5 bg-tealTertiary/50 rounded-full blur-sm animate-bounce" style={{top: '35%', left: '80%', animationDelay: '2s', animationDuration: '3.5s'}}></div>
        <div className="absolute w-1.5 h-1.5 bg-emeraldLight/80 rounded-full blur-sm animate-bounce" style={{bottom: '30%', left: '20%', animationDelay: '0.5s', animationDuration: '5s'}}></div>
        <div className="absolute w-2 h-2 bg-cyanSecondary/60 rounded-full blur-sm animate-bounce" style={{bottom: '15%', right: '25%', animationDelay: '2.5s', animationDuration: '4.5s'}}></div>
      </div>

      {/* Header */}
      <div
        className="relative z-10 flex items-center justify-between p-5 cursor-pointer overflow-hidden group"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          toggleExpanded();
        }}
      >
        {/* Efeito Shine */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>

        <div className="flex items-center gap-4 relative z-10">
          <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-emeraldBase/40 via-cyanPrimary/30 to-tealPrimary/40 shadow-lg border border-emeraldMedium/60">
            <Calendar className="w-6 h-6 text-emeraldLight" />
            <div className="absolute inset-0 bg-gradient-to-br from-emeraldMedium/10 to-cyanTertiary/10 rounded-xl animate-pulse-subtle"></div>
          </div>
          <div>
            <h3 className="text-white font-semibold text-lg">
              Google Calendar
            </h3>
            <p className="text-white text-sm">Integração inteligente com IA para agendamentos automáticos</p>
          </div>
        </div>

        <div className="flex items-center gap-2 relative z-10">
          {calendarStatus.connected && (
            <div className="relative flex items-center gap-1 px-3 py-1.5 bg-grayBase/80 text-emeraldMedium rounded-lg text-xs font-medium border border-emeraldMedium shadow-sm animate-pulse-subtle">
              <div className="absolute inset-0 bg-grayBase/60 rounded-lg animate-pulse" style={{animationDuration: '3s'}}></div>
              <CheckCircle className="relative z-10 w-3 h-3 text-emeraldMedium" />
              <span className="relative z-10 text-emeraldMedium">Conectado</span>
            </div>
          )}

          {calendarStatus.isAuthenticating && (
            <div className="flex items-center gap-1 px-2 py-1 bg-yellowBase/20 text-yellowMedium rounded-md text-xs">
              <RefreshCw className="w-3 h-3 animate-spin" />
              Autenticando
            </div>
          )}

          {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-grayText" />
            ) : (
              <ChevronDown className="w-5 h-5 text-grayText" />
            )}
        </div>
      </div>

      {/* Content */}
      <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
        isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className={`relative z-10 px-5 pb-5 pt-8 transform transition-all duration-400 ${
          isExpanded ? 'translate-y-0 scale-100' : '-translate-y-4 scale-98'
        } ${(isMinScheduleExpanded || isMeetingDurationExpanded || isWorkingHoursExpanded) ? 'max-h-[1800px] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-grayContent/50 [&::-webkit-scrollbar-track]:rounded-lg [&::-webkit-scrollbar-thumb]:bg-gradient-to-b [&::-webkit-scrollbar-thumb]:from-emeraldBase/60 [&::-webkit-scrollbar-thumb]:to-cyanPrimary/60 [&::-webkit-scrollbar-thumb]:rounded-lg [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-grayMedium/50 hover:[&::-webkit-scrollbar-thumb]:from-emeraldMedium/80 hover:[&::-webkit-scrollbar-thumb]:to-cyanTertiary/80' : 'overflow-visible'}`}>

          {/* Aviso importante sobre Gemini e GPT */}
          {!(settings?.AI_SELECTED === 'GEMINI' || settings?.AI_SELECTED === 'GPT' || settings?.AI_SELECTED === 'PUREROUTER') && (
            <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-300">
                    Disponível apenas com Gemini, GPT ou oZap AI
                  </p>
                  <p className="text-xs text-amber-200/80 mt-1">
                    O agendamento automático funciona com Gemini, GPT ou oZap AI.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 mt-4">
            {/* Connection Status */}
            <div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 rounded-xl p-4 border border-emeraldMedium/50 shadow-lg hover:shadow-emeraldBase/25 transition-all duration-300 group hover:scale-105 hover:-translate-y-1">
              <div className="absolute inset-0 bg-gradient-to-br from-emeraldMedium/10 via-cyanTertiary/10 to-tealTertiary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {calendarStatus.connected ? (
                      <div className="p-1 rounded-full bg-emeraldBase/20 group-hover:bg-emeraldBase/30 transition-colors duration-300">
                        <CheckCircle className="w-4 h-4 text-emeraldMedium group-hover:text-emeraldLight transition-colors duration-300" />
                      </div>
                    ) : (
                      <div className="p-1 rounded-full bg-statusWarning/20 group-hover:bg-statusWarning/30 transition-colors duration-300">
                        <AlertCircle className="w-4 h-4 text-statusWarning group-hover:text-statusWarning transition-colors duration-300" />
                      </div>
                    )}
                    <span className="text-sm font-medium text-white group-hover:text-emeraldLight transition-colors duration-300">Status</span>
                  </div>
                </div>
                <p className="text-white font-semibold text-lg group-hover:text-emeraldLight transition-colors duration-300">
                  {calendarStatus.connected ? 'Conectado' : 'Desconectado'}
                </p>
                {calendarStatus.userEmail && (
                  <p className="text-xs text-white/80 mt-1 truncate group-hover:text-emeraldLight/80 transition-colors duration-300">{calendarStatus.userEmail}</p>
                )}
              </div>
            </div>

            {/* Available Slots */}
            <div
              className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 rounded-xl p-4 border border-emeraldMedium/50 shadow-lg hover:shadow-emeraldBase/25 transition-all duration-300 group hover:scale-105 hover:-translate-y-1 cursor-pointer"
              onClick={async () => {
                console.log('🔄 [DEBUG] Forçando atualização COMPLETA dos dados do calendário...');

                // Mostra estado atual antes da atualização
                console.log('📊 [DEBUG] Estado ANTES da atualização:');
                console.log(`   📅 Slots disponíveis: ${calendarStatus.availableSlots}`);
                console.log(`   📋 Eventos próximos: ${calendarStatus.upcomingMeetings}`);

                // Força atualização
                setIsLoadingCalendarData(true);
                await loadCalendarData(true);

                // Faz uma segunda chamada direta para debugging
                try {
                  const debugEvents = await ipcRenderer.invoke('google-calendar-get-upcoming-events', {
                    days: 1, // 🆕 Debug apenas do dia atual
                    instanceId
                  });

                  const debugStartDate = new Date();
                  debugStartDate.setHours(0, 0, 0, 0); // Início do dia atual
                  const debugEndDate = new Date();
                  debugEndDate.setHours(23, 59, 59, 999); // Final do dia atual

                  const debugSlots = await ipcRenderer.invoke('google-calendar-get-available-slots', {
                    startDate: debugStartDate.toISOString(),
                    endDate: debugEndDate.toISOString(),
                    instanceId
                  });

                  console.log('🔍 [DEBUG] Dados ATUALIZADOS do Google Calendar (APENAS HOJE):');
                  console.log('   📋 Eventos hoje:', debugEvents.count);
                  console.log('   📅 Slots disponíveis hoje:', debugSlots.count);
                  console.log('   🎯 RESULTADO: Exibindo apenas dados do dia atual');

                } catch (error) {
                  console.error('❌ [DEBUG] Erro ao buscar dados:', error);
                }
                setIsLoadingCalendarData(false);
              }}
              title="Clique para atualizar os horários (debug)"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emeraldMedium/10 via-cyanTertiary/10 to-tealTertiary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1 rounded-full bg-cyanPrimary/20 group-hover:bg-cyanPrimary/30 transition-colors duration-300">
                    <CalendarDays className="w-4 h-4 text-cyanTertiary group-hover:text-cyanSecondary transition-colors duration-300" />
                  </div>
                  <span className="text-sm font-medium text-white group-hover:text-emeraldLight transition-colors duration-300">Horários Livres</span>
                  <div className="relative group/tooltip inline-flex">
                    <Info className="w-4 h-4 text-cyanTertiary hover:text-white cursor-help transition-colors duration-200" />
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 hidden group-hover/tooltip:block w-72 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-2xl border border-cyanTertiary/30 pointer-events-none" style={{zIndex: 9999}}>
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-gray-800"></div>
                      <div className="text-center leading-relaxed font-medium">
                        Horários disponíveis considerando tempo mínimo para agendar + duração da reunião
                      </div>
                    </div>
                  </div>
                </div>
                {calendarStatus.connected ? (
                  <>
                    <p className="text-white font-semibold text-2xl group-hover:text-emeraldLight transition-colors duration-300">{calendarStatus.availableSlots}</p>
                    <p className="text-xs text-white/80 group-hover:text-emeraldLight/80 transition-colors duration-300">Hoje</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5">
                      <p className="text-white/70 font-medium text-sm group-hover:text-emeraldLight/80 transition-colors duration-300">Conecte o calendário</p>
                      <Link className="w-3 h-3 text-white/50 group-hover:text-emeraldLight/60 transition-colors duration-300" />
                    </div>
                    <p className="text-xs text-white/60 group-hover:text-emeraldLight/60 transition-colors duration-300">Para ver horários livres</p>
                  </>
                )}
              </div>
            </div>

            {/* Upcoming Meetings */}
            <div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 rounded-xl p-4 border border-emeraldMedium/50 shadow-lg hover:shadow-emeraldBase/25 transition-all duration-300 group hover:scale-105 hover:-translate-y-1">
              <div className="absolute inset-0 bg-gradient-to-br from-emeraldMedium/10 via-cyanTertiary/10 to-tealTertiary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1 rounded-full bg-tealPrimary/20 group-hover:bg-tealPrimary/30 transition-colors duration-300">
                    <CalendarCheck className="w-4 h-4 text-tealTertiary group-hover:text-tealSecondary transition-colors duration-300" />
                  </div>
                  <span className="text-sm font-medium text-white group-hover:text-emeraldLight transition-colors duration-300">Reuniões Agendadas</span>
                </div>
                {calendarStatus.connected ? (
                  <>
                    <p className="text-white font-semibold text-2xl group-hover:text-emeraldLight transition-colors duration-300">{calendarStatus.upcomingMeetings}</p>
                    <p className="text-xs text-white/80 group-hover:text-emeraldLight/80 transition-colors duration-300">Hoje</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5">
                      <p className="text-white/70 font-medium text-sm group-hover:text-emeraldLight/80 transition-colors duration-300">Conecte o calendário</p>
                      <Link className="w-3 h-3 text-white/50 group-hover:text-emeraldLight/60 transition-colors duration-300" />
                    </div>
                    <p className="text-xs text-white/60 group-hover:text-emeraldLight/60 transition-colors duration-300">Para ver reuniões agendadas</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {calendarStatus.connected && (
            <>
              {/* Function Calling Toggle */}
              <div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 border border-emeraldMedium/50 rounded-xl p-5 mb-6 shadow-lg">

                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emeraldBase/40 via-cyanPrimary/30 to-tealPrimary/40 shadow-lg border border-emeraldMedium/60">
                      <Bot className="w-5 h-5 text-emeraldLight" />
                      <div className="absolute inset-0 bg-gradient-to-br from-emeraldMedium/10 to-cyanTertiary/10 rounded-xl animate-pulse-subtle"></div>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">Agendamento Automático por IA</h4>
                      <p className="text-sm text-white">
                        {(settings?.AI_SELECTED !== 'GEMINI' && settings?.AI_SELECTED !== 'GPT' && settings?.AI_SELECTED !== 'PUREROUTER') ?
                          'Disponível com Gemini, GPT ou oZap AI' :
                          functionCallingEnabled
                            ? 'IA pode agendar reuniões automaticamente'
                            : 'IA responde normalmente sem agendar'
                        }
                      </p>
                    </div>
                  </div>

                  <div className={`flex items-center gap-4 ${knowledgeBaseActive ? 'opacity-60' : ''}`}>
                    {(functionCallingEnabled && !knowledgeBaseActive) ? (
                                  <div className="relative flex items-center gap-1 px-3 py-1.5 bg-grayBase/80 text-emeraldMedium rounded-lg text-xs font-medium border border-emeraldMedium shadow-sm animate-pulse-subtle">
                  <div className="absolute inset-0 bg-grayBase/60 rounded-lg animate-pulse" style={{animationDuration: '3s'}}></div>
                  <CheckCircle className="relative z-10 w-3 h-3 text-emeraldMedium" />
                  <span className="relative z-10 text-emeraldMedium">Ativo</span>
                </div>
                    ) : (
                      <span className="text-sm font-semibold px-2 py-1 rounded-full text-grayText bg-grayMedium/20 border border-grayLight/30">
                        Inativo
                      </span>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleFunctionCalling();
                      }}
                      disabled={!(settings?.AI_SELECTED === 'GEMINI' || settings?.AI_SELECTED === 'GPT' || settings?.AI_SELECTED === 'PUREROUTER') || knowledgeBaseActive}
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 shadow-lg ${
                        !(settings?.AI_SELECTED === 'GEMINI' || settings?.AI_SELECTED === 'GPT' || settings?.AI_SELECTED === 'PUREROUTER') || knowledgeBaseActive
                          ? 'bg-grayLight/50 shadow-grayLight/25 cursor-not-allowed opacity-50'
                          : functionCallingEnabled
                            ? 'bg-gradient-to-r from-emeraldBase/80 via-emeraldMedium to-cyanPrimary shadow-cyanPrimary/25 focus:outline-none focus:ring-2 focus:ring-cyanTertiary/50 focus:ring-offset-2 focus:ring-offset-transparent'
                            : 'bg-grayMedium shadow-grayMedium/25 focus:outline-none focus:ring-2 focus:ring-cyanTertiary/50 focus:ring-offset-2 focus:ring-offset-transparent'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full shadow-lg transition-transform duration-300 ${
                          !(settings?.AI_SELECTED === 'GEMINI' || settings?.AI_SELECTED === 'GPT' || settings?.AI_SELECTED === 'PUREROUTER') || knowledgeBaseActive
                            ? 'bg-graySubtle translate-x-1'
                            : functionCallingEnabled
                              ? 'bg-white translate-x-6'
                              : 'bg-white translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="relative z-10 mt-4 text-xs text-white">
                  {!(settings?.AI_SELECTED === 'GEMINI' || settings?.AI_SELECTED === 'GPT' || settings?.AI_SELECTED === 'PUREROUTER') ? (
                    <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-400/20 rounded-lg">
                      <AlertCircle className="w-3 h-3 text-amber-400" />
                      <span>Disponível apenas com Gemini, GPT ou oZap AI</span>
                    </div>
                  ) : (functionCallingEnabled && !knowledgeBaseActive) ? (
                    <div className="flex items-center gap-2 p-2 bg-emeraldBase/10 border border-emeraldMedium/20 rounded-lg">
                      <CheckCircle className="w-3 h-3 text-emeraldMedium" />
                      <span>Quando ativo, a IA pode detectar solicitações de reunião e agendar automaticamente</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-2 bg-statusWarning/10 border border-statusWarning/20 rounded-lg">
                      <AlertCircle className="w-3 h-3 text-statusWarning" />
                      <span>Quando inativo, a IA responde normalmente sem usar funções de calendário</span>
                    </div>
                  )}
                </div>

                {knowledgeBaseActive && (
                  <div className="relative z-10 mt-4 flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>Funções de calendário temporariamente desabilitadas enquanto a Base de Conhecimento está ativa (limitação da API Gemini)</span>
                  </div>
                )}
              </div>
            </>
          )}

          {calendarStatus.connected && (
            <>
              <div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 border border-emeraldMedium/50 rounded-xl p-5 mb-6 shadow-lg">
            <div className="relative z-10 flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emeraldBase/40 via-cyanPrimary/30 to-tealPrimary/40 shadow-lg border border-emeraldMedium/60">
                  <Clock className="w-5 h-5 text-emeraldLight" />
                  <div className="absolute inset-0 bg-gradient-to-br from-emeraldMedium/10 to-cyanTertiary/10 rounded-xl animate-pulse-subtle"></div>
                </div>
                <div>
                  <h4 className="text-white font-semibold">Tempo Mínimo para Agendamento</h4>
                  <p className="text-sm text-white">
                    Define o tempo mínimo necessário entre agora e o agendamento
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-grayText" />
                  <span className="text-sm text-white font-medium">
                    {MIN_SCHEDULE_TIME_OPTIONS[minScheduleTime]}
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsMinScheduleExpanded(!isMinScheduleExpanded);
                  }}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-emeraldBase/20 hover:bg-emeraldBase/30 transition-colors duration-200"
                  title={isMinScheduleExpanded ? 'Minimizar' : 'Expandir configurações'}
                >
                  {isMinScheduleExpanded ? (
                    <ChevronUp className="w-4 h-4 text-emeraldMedium" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-emeraldMedium" />
                  )}
                </button>
              </div>
            </div>

            <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
              isMinScheduleExpanded ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'
            }`}>
              <div className={`space-y-1 transform transition-all duration-300 ${
                isMinScheduleExpanded ? 'translate-y-0 scale-100' : '-translate-y-2 scale-95'
              }`}>
              {MIN_SCHEDULE_TIME_CONFIG.map((option) => (
                <button
                  key={option.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleMinScheduleTimeChange(option.value);
                  }}
                  className={`w-full flex items-center justify-between p-2 rounded-md text-sm transition-all duration-200 ${
                    minScheduleTime === option.value
                      ? 'bg-gradient-to-r from-emeraldBase/20 to-cyanPrimary/20 border border-emeraldBase/40 text-white shadow-md'
                      : 'bg-grayMedium/20 border border-grayLight/20 text-white hover:bg-grayLight/30 hover:border-grayLight/30 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {minScheduleTime === option.value ? (
                      <CheckCircle className="w-3 h-3 text-emeraldMedium" />
                    ) : (
                      <div className="w-3 h-3 rounded-full border border-grayLight"></div>
                    )}
                    <span className="font-medium text-sm">{option.label}</span>
                  </div>
                  <span className="text-xs text-white/70">{option.desc}</span>
                </button>
              ))}
              </div>
            </div>

            <div className="mt-3 text-xs text-white">
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3 text-emeraldMedium" />
                <span>
                  A IA só poderá agendar reuniões com pelo menos <strong className="text-white">{MIN_SCHEDULE_TIME_OPTIONS[minScheduleTime]}</strong> de antecedência
                </span>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 border border-emeraldMedium/50 rounded-xl p-5 mb-6 shadow-lg">
            <div className="relative z-10 flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emeraldLight/40 via-cyanTertiary/30 to-emeraldMedium/40 shadow-lg border border-emeraldMedium/60">
                  <CalendarCheck className="w-5 h-5 text-emeraldMedium" />
                  <div className="absolute inset-0 bg-gradient-to-br from-emeraldBase/10 to-cyanPrimary/10 rounded-xl animate-pulse-subtle"></div>
                </div>
                <div>
                  <h4 className="text-white font-semibold">Duração Padrão das Reuniões</h4>
                  <p className="text-sm text-white">
                    Define quanto tempo cada reunião deve durar por padrão
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-grayText" />
                  <span className="text-sm text-white font-medium">
                    {MEETING_DURATION_OPTIONS[meetingDuration]}
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsMeetingDurationExpanded(!isMeetingDurationExpanded);
                  }}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-emeraldBase/20 hover:bg-emeraldBase/30 transition-colors duration-200"
                  title={isMeetingDurationExpanded ? 'Minimizar' : 'Expandir configurações'}
                >
                  {isMeetingDurationExpanded ? (
                    <ChevronUp className="w-4 h-4 text-emeraldMedium" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-emeraldMedium" />
                  )}
                </button>
              </div>
            </div>

            <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
              isMeetingDurationExpanded ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'
            }`}>
              <div className={`space-y-1 transform transition-all duration-300 ${
                isMeetingDurationExpanded ? 'translate-y-0 scale-100' : '-translate-y-2 scale-95'
              }`}>
              {MEETING_DURATION_CONFIG.map((option) => (
                <button
                  key={option.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleMeetingDurationChange(option.value);
                  }}
                  className={`w-full flex items-center justify-between p-3 rounded-md text-sm transition-all duration-200 ${
                    meetingDuration === option.value
                      ? 'bg-gradient-to-r from-emeraldBase/20 to-cyanPrimary/20 border border-emeraldBase/40 text-white shadow-md'
                      : 'bg-grayMedium/20 border border-grayLight/20 text-white hover:bg-grayLight/30 hover:border-grayLight/30 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {meetingDuration === option.value ? (
                      <CheckCircle className="w-4 h-4 text-emeraldMedium" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border border-grayLight"></div>
                    )}
                    <span className="font-medium text-sm">{option.label}</span>
                  </div>
                  <span className="text-xs text-white/70">{option.desc}</span>
                </button>
              ))}
              </div>
            </div>

            <div className="mt-3 text-xs text-white">
              <div className="flex items-center gap-1">
                                  <CalendarCheck className="w-3 h-3 text-emeraldMedium" />
                <span>
                  Todas as reuniões criadas pela IA terão duração de <strong className="text-white">{MEETING_DURATION_OPTIONS[meetingDuration]}</strong> por padrão
                </span>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 border border-emeraldMedium/50 rounded-xl p-5 mb-6 shadow-lg">
            <div className="relative z-10 flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emeraldBase/40 via-cyanPrimary/30 to-tealPrimary/40 shadow-lg border border-emeraldMedium/60">
                  <Timer className="w-5 h-5 text-emeraldLight" />
                  <div className="absolute inset-0 bg-gradient-to-br from-emeraldMedium/10 to-cyanTertiary/10 rounded-xl animate-pulse-subtle"></div>
                </div>
                <div>
                  <h4 className="text-white font-semibold">Horários de Funcionamento</h4>
                  <p className="text-sm text-white">
                    Configure os dias e horários disponíveis para agendamentos
                  </p>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setIsWorkingHoursExpanded(!isWorkingHoursExpanded);
                }}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-emeraldBase/20 hover:bg-emeraldBase/30 transition-colors duration-200"
                title={isWorkingHoursExpanded ? 'Minimizar' : 'Expandir configurações'}
              >
                {isWorkingHoursExpanded ? (
                  <ChevronUp className="w-4 h-4 text-emeraldMedium" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-emeraldMedium" />
                )}
              </button>
            </div>

            <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
              isWorkingHoursExpanded ? 'max-h-[800px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'
            }`}>
              <div className={`space-y-4 transform transition-all duration-300 ${
                isWorkingHoursExpanded ? 'translate-y-0 scale-100' : '-translate-y-2 scale-95'
              }`}>
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      applyWorkingHoursPreset('business');
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-emeraldBase/20 text-emeraldMedium rounded-md text-xs font-medium hover:bg-emeraldBase/30 transition-colors duration-200"
                  >
                    <Sun className="w-3 h-3" />
                    Comercial (8h-18h)
                  </button>
                </div>

                <div className="space-y-3">
                  {WEEKDAYS_CONFIG.map((day) => (
                    <div
                      key={day.key}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-200 ${
                        workingHours[day.key].enabled
                                            ? 'bg-emeraldBase/10 border-emeraldBase/30'
                  : 'bg-grayMedium/20 border-grayLight/20'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            toggleDayEnabled(day.key);
                          }}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${
                            workingHours[day.key].enabled
                                                          ? 'bg-emeraldBase'
                            : 'bg-grayLight'
                          }`}
                        >
                          <span
                            className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200 ${
                              workingHours[day.key].enabled ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <div>
                          <span                             className={`text-sm font-medium ${
                              workingHours[day.key].enabled ? 'text-white' : 'text-grayText'
                            }`}>
                            {day.label}
                          </span>
                          <span className={`ml-2 text-xs ${
                            workingHours[day.key].enabled ? 'text-white' : 'text-grayLight'
                          }`}>
                            {day.short}
                          </span>
                        </div>
                      </div>

                      {workingHours[day.key].enabled && (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-grayText">Das</span>
                            <input
                              type="time"
                              value={workingHours[day.key].start}
                              onChange={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleDayTimeChange(day.key, 'start', e.target.value);
                              }}
                              className="px-2 py-1 bg-emeraldBase/20 border border-emeraldBase/50 rounded text-white text-xs w-20 focus:border-emeraldMedium focus:bg-emeraldBase/30 focus:outline-none transition-colors duration-200 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:opacity-0"
                            />
                          </div>
                          <span className="text-grayText text-xs">às</span>
                          <div className="flex items-center gap-1">
                            <input
                              type="time"
                              value={workingHours[day.key].end}
                              onChange={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleDayTimeChange(day.key, 'end', e.target.value);
                              }}
                              className="px-2 py-1 bg-emeraldBase/20 border border-emeraldBase/50 rounded text-white text-xs w-20 focus:border-emeraldMedium focus:bg-emeraldBase/30 focus:outline-none transition-colors duration-200 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:opacity-0"
                            />
                          </div>
                        </div>
                      )}

                      {!workingHours[day.key].enabled && (
                        <span className="text-xs text-grayLight">
                          Indisponível
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-3 text-xs text-white">
              <div className="flex items-center gap-1">
                                  <Timer className="w-3 h-3 text-emeraldMedium" />
                <span>
                  A IA só poderá agendar reuniões nos dias e horários habilitados
                </span>
              </div>
            </div>
          </div>

          {/* Mensagem de Sucesso Personalizada */}
          <div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 border border-emeraldMedium/50 rounded-xl p-5 mb-6 shadow-lg">
            <div className="relative z-10 flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emeraldLight/40 via-cyanTertiary/30 to-emeraldMedium/40 shadow-lg border border-emeraldMedium/60">
                  <MessageSquareText className="w-5 h-5 text-emeraldMedium" />
                  <div className="absolute inset-0 bg-gradient-to-br from-emeraldBase/10 to-cyanPrimary/10 rounded-xl animate-pulse-subtle"></div>
                </div>
                <div>
                  <h4 className="text-white font-semibold">Mensagem de Agendamento</h4>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleCustomMessage();
                }}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 shadow-lg ${
                  customMessageEnabled
                    ? 'bg-gradient-to-r from-emeraldBase/80 via-emeraldBase to-emeraldMedium shadow-emeraldMedium/25 focus:outline-none focus:ring-2 focus:ring-emeraldLight/50 focus:ring-offset-2 focus:ring-offset-transparent'
                    : 'bg-grayMedium shadow-grayMedium/25 focus:outline-none focus:ring-2 focus:ring-emeraldLight/50 focus:ring-offset-2 focus:ring-offset-transparent'
                }`}
                title={customMessageEnabled ? 'Desativar mensagem personalizada' : 'Ativar mensagem personalizada'}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full shadow-lg transition-transform duration-300 ${
                    customMessageEnabled
                      ? 'bg-white translate-x-6'
                      : 'bg-white translate-x-1'
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm text-white">
                {customMessageEnabled
                  ? "Personalize a mensagem enviada quando uma reunião é agendada"
                  : "Usar mensagem padrão completa para agendamentos"
                }
              </p>
              {customMessageEnabled && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsSuccessMessageExpanded(!isSuccessMessageExpanded);
                  }}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-emeraldBase/20 hover:bg-emeraldBase/30 transition-colors duration-200"
                  title={isSuccessMessageExpanded ? 'Minimizar' : 'Expandir configurações'}
                >
                  {isSuccessMessageExpanded ? (
                    <ChevronUp className="w-4 h-4 text-emeraldMedium" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-emeraldMedium" />
                  )}
                </button>
              )}
            </div>

            {customMessageEnabled && (
              <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
                isSuccessMessageExpanded ? 'max-h-[600px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'
              }`}>
              <div className={`space-y-4 transform transition-all duration-300 ${
                isSuccessMessageExpanded ? 'translate-y-0 scale-100' : '-translate-y-2 scale-95'
              }`}>
                <div>
                  <label className="block text-sm font-medium text-graySubtle mb-2">
                    Mensagem personalizada:
                  </label>
                  <textarea
                    value={successMessage}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleSuccessMessageChange(e.target.value);
                    }}
                    placeholder="*CONVITE PARA REUNIÃO*\n\n📋 *Assunto:* Reunião com {NOME_PARTICIPANTE} - {DATA_FORMATADA} às {HORA}\n📅 *Data:* {DATA_COMPLETA}\n⏰ *Horário:* {HORA}\n\n🔗 *Link do Google Meet:*\n{LINK_REUNIAO}\n\n📧 *Confirmação:* Enviada para: {EMAIL_PARTICIPANTE}\n⏰ *Lembretes:* Você receberá notificações antes da reunião\n\n✅ *Para participar:* Clique no link na hora marcada\n📱 *Pelo celular:* Baixe o app Google Meet\n💻 *Pelo computador:* Acesse direto pelo navegador\n\nNos vemos lá! 😊"
                    className="w-full p-3 bg-dashboardBg border border-primaryColor/30 rounded-lg text-white text-sm resize-vertical min-h-[200px]"
                    rows={12}
                  />
                  <div className="text-xs text-grayLight mt-2 space-y-1">
                    <p>💡 <strong>Formatação:</strong> Use *texto* para negrito e \n para quebras de linha</p>
                    <p>🔧 <strong>Placeholders disponíveis:</strong></p>
                    <div className="ml-4 space-y-0.5">
                      <p>• {"{NOME_PARTICIPANTE}"} - Nome do participante</p>
                      <p>• {"{DATA_FORMATADA}"} - Data (01/08/2025)</p>
                      <p>• {"{DATA_COMPLETA}"} - Data completa (sexta-feira, 01 de agosto)</p>
                      <p>• {"{HORA}"} - Horário (14:30)</p>
                      <p>• {"{LINK_REUNIAO}"} - Link da reunião (se Google Meet ativo) ou texto alternativo</p>
                      <p>• {"{EMAIL_PARTICIPANTE}"} - Email do participante (quando fornecido)</p>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            )}

            <div className="mt-3 text-xs text-white">
              <div className="flex items-center gap-1">
                <MessageSquareText className="w-3 h-3 text-emeraldMedium" />
                <span>
                  {customMessageEnabled
                    ? "Esta mensagem personalizada será enviada quando uma reunião for agendada"
                    : "Será enviada mensagem padrão com informações completas da reunião (assunto, data, horário, link do Meet, etc.)"
                  }
                </span>
              </div>
            </div>
          </div>
            </>
          )}

          {/* 📝 TÍTULO PERSONALIZADO - Card Independente */}
          {functionCallingEnabled && calendarStatus.connected && (
            <div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 border border-emeraldMedium/50 rounded-xl p-5 mb-6 shadow-lg">
              <div className="relative z-10 flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emeraldLight/40 via-cyanTertiary/30 to-emeraldMedium/40 shadow-lg border border-emeraldMedium/60">
                    <Type className="w-5 h-5 text-emeraldMedium" />
                    <div className="absolute inset-0 bg-gradient-to-br from-emeraldBase/10 to-cyanPrimary/10 rounded-xl animate-pulse-subtle"></div>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Título Personalizado</h4>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleCustomTitle();
                  }}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 shadow-lg ${
                    customTitleEnabled
                      ? 'bg-gradient-to-r from-emeraldBase/80 via-emeraldBase to-emeraldMedium shadow-emeraldMedium/25 focus:outline-none focus:ring-2 focus:ring-emeraldLight/50 focus:ring-offset-2 focus:ring-offset-transparent'
                      : 'bg-grayMedium shadow-grayMedium/25 focus:outline-none focus:ring-2 focus:ring-emeraldLight/50 focus:ring-offset-2 focus:ring-offset-transparent'
                  }`}
                  title={customTitleEnabled ? 'Desativar título personalizado' : 'Ativar título personalizado'}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full shadow-lg transition-transform duration-300 ${
                      customTitleEnabled
                        ? 'bg-white translate-x-6'
                        : 'bg-white translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-white">
                  {customTitleEnabled
                    ? "Personalize o título das reuniões criadas no Google Calendar"
                    : "Usar título padrão automático para as reuniões"
                  }
                </p>
                {customTitleEnabled && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setIsMeetingTitleExpanded(!isMeetingTitleExpanded);
                    }}
                    className="flex items-center justify-center w-8 h-8 rounded-lg bg-emeraldBase/20 hover:bg-emeraldBase/30 transition-colors duration-200"
                    title={isMeetingTitleExpanded ? 'Minimizar' : 'Expandir configurações'}
                  >
                    {isMeetingTitleExpanded ? (
                      <ChevronUp className="w-4 h-4 text-emeraldMedium" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-emeraldMedium" />
                    )}
                  </button>
                )}
              </div>

              {customTitleEnabled && (
                <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
                  isMeetingTitleExpanded ? 'max-h-[400px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'
                }`}>
                <div className={`space-y-4 transform transition-all duration-300 ${
                  isMeetingTitleExpanded ? 'translate-y-0 scale-100' : '-translate-y-2 scale-95'
                }`}>
                  <div>
                    <label className="block text-sm font-medium text-graySubtle mb-2">
                      Título personalizado:
                    </label>
                    <textarea
                      value={meetingTitle}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleMeetingTitleChange(e.target.value);
                      }}
                      placeholder="Reunião com {NOME_PARTICIPANTE} - {DATA_FORMATADA} às {HORA}"
                      className="w-full p-3 bg-dashboardBg border border-primaryColor/30 rounded-lg text-white text-sm resize-vertical min-h-[80px]"
                      rows={3}
                    />
                    <div className="text-xs text-grayLight mt-2 space-y-1">
                      <p>🔧 <strong>Placeholders disponíveis:</strong></p>
                      <div className="ml-4 space-y-0.5">
                        <p>• {"{"}NOME_PARTICIPANTE{"}"} - Nome do participante</p>
                        <p>• {"{"}DATA_FORMATADA{"}"} - Data (01/08/2025)</p>
                        <p>• {"{"}DATA_COMPLETA{"}"} - Data completa (sexta-feira, 01 de agosto)</p>
                        <p>• {"{"}HORA{"}"} - Horário (14:30)</p>
                        <p>• {"{"}EMAIL_PARTICIPANTE{"}"} - Email do participante (quando fornecido)</p>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              )}

              <div className="mt-3 text-xs text-white">
                <div className="flex items-center gap-1">
                  <Type className="w-3 h-3 text-emeraldMedium" />
                  <span>
                    {customTitleEnabled
                      ? "Este título personalizado será usado nas reuniões criadas no Google Calendar"
                      : "Será usado título padrão automático baseado no participante e data"
                    }
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* 📧 E-MAIL OBRIGATÓRIO - Card Independente */}
          {functionCallingEnabled && calendarStatus.connected && (
            <div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 border border-emeraldMedium/50 rounded-xl p-5 mb-6 shadow-lg">
              <div className="relative z-10 flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emeraldLight/40 via-cyanTertiary/30 to-emeraldMedium/40 shadow-lg border border-emeraldMedium/60">
                    <Mail className="w-5 h-5 text-emeraldMedium" />
                    <div className="absolute inset-0 bg-gradient-to-br from-emeraldBase/10 to-cyanPrimary/10 rounded-xl animate-pulse-subtle"></div>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">E-mail Obrigatório</h4>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleEmailRequired();
                  }}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 shadow-lg ${
                    emailRequired
                      ? 'bg-gradient-to-r from-emeraldBase/80 via-emeraldMedium to-cyanPrimary shadow-cyanPrimary/25 focus:outline-none focus:ring-2 focus:ring-cyanTertiary/50 focus:ring-offset-2 focus:ring-offset-transparent'
                      : 'bg-grayMedium shadow-grayMedium/25 focus:outline-none focus:ring-2 focus:ring-cyanTertiary/50 focus:ring-offset-2 focus:ring-offset-transparent'
                  }`}
                  title={emailRequired ? 'Desativar e-mail obrigatório' : 'Ativar e-mail obrigatório'}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full shadow-lg transition-transform duration-300 ${
                      emailRequired
                        ? 'bg-white translate-x-6'
                        : 'bg-white translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="text-xs text-white">
                <div className="space-y-1">
                  <p>📧 <strong>Quando ATIVO:</strong> E-mail é obrigatório para agendar reuniões e sempre cria Google Meet</p>
                  <p>🎯 <strong>Quando INATIVO:</strong> E-mail é opcional e não cria Google Meet automaticamente</p>
                  <p>💻 <strong>Comportamento:</strong> Controla se o sistema exige e-mail do participante para criar convites</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="relative z-10 flex flex-wrap gap-4 mb-6">
            {!calendarStatus.connected ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowTokenModal(true);
                }}
                className="relative overflow-hidden flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-emeraldBase via-cyanPrimary to-tealPrimary text-white rounded-xl font-semibold shadow-lg hover:shadow-emeraldMedium/25 transition-all duration-300 group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-emeraldBase/80 via-cyanPrimary/80 to-tealPrimary/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <ExternalLink className="relative z-10 w-5 h-5" />
                <span className="relative z-10">Conectar Google Calendar</span>
              </button>
            ) : (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleRefresh();
                  }}
                  disabled={isLoading}
                  className="relative overflow-hidden flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emeraldBase/20 to-cyanPrimary/20 text-emeraldMedium border border-emeraldMedium/30 rounded-xl font-medium hover:from-emeraldBase/30 hover:to-cyanPrimary/30 transition-all duration-300 disabled:opacity-50 group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-emeraldBase/10 to-cyanPrimary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <RefreshCw className={`relative z-10 w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  <span className="relative z-10">{isLoading ? 'Sincronizando...' : 'Atualizar'}</span>
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDisconnect();
                  }}
                  className="relative overflow-hidden flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500/20 to-rose-500/20 text-red-300 border border-red-400/30 rounded-xl font-medium hover:from-red-500/30 hover:to-rose-500/30 transition-all duration-300 group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-red-400/10 to-rose-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <CalendarX className="relative z-10 w-4 h-4" />
                  <span className="relative z-10">Desconectar</span>
                </button>
              </>
            )}
          </div>

          {/* Last Sync Info */}
          {calendarStatus.lastSync && (
            <div className="flex items-center justify-between text-sm text-grayText bg-dashboardBg/30 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Última sincronização:</span>
              </div>
              <span>{calendarStatus.lastSync.toLocaleString('pt-BR')}</span>
            </div>
          )}

          {/* Help Text */}
          {!calendarStatus.connected && (
            <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-orange-100">
                  <p className="font-medium mb-1">Conecte seu Google Calendar para:</p>
                  <ul className="text-xs space-y-0.5 text-orange-200/80">
                    <li>• Permitir agendamento automático via WhatsApp</li>
                    <li>• Sincronizar horários disponíveis em tempo real</li>
                    <li>• Gerenciar reuniões diretamente pelo {APP_NAME}</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dashboardCard rounded-xl p-6 max-w-md w-full mx-4 border border-primaryColor/20">
            <h3 className="text-white font-semibold text-lg mb-4">Conectar Google Calendar</h3>

            {calendarStatus.authUrl ? (
              <div>
                <p className="text-graySubtle text-sm mb-4">
                  1. Uma nova aba foi aberta no seu navegador para autorização
                </p>
                <p className="text-graySubtle text-sm mb-4">
                  2. Após autorizar, copie o código de autorização e cole aqui:
                </p>

                <input
                  type="text"
                  value={authCode}
                  onChange={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setAuthCode(e.target.value);
                  }}
                  placeholder="Cole o código de autorização aqui..."
                  className="w-full p-3 bg-dashboardBg border border-primaryColor/30 rounded-lg text-white mb-4"
                  disabled={isLoading}
                />

                <div className="flex gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleAuthCode();
                    }}
                    disabled={isLoading || !authCode.trim()}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg py-2 font-medium hover:from-blue-600 hover:to-purple-600 transition-all duration-200 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Processando...
                      </div>
                    ) : (
                      'Conectar'
                    )}
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (calendarStatus.authUrl) {
                        if (window.require) {
                          const { shell } = window.require('electron');
                          shell.openExternal(calendarStatus.authUrl);
                        } else {
                          window.open(calendarStatus.authUrl, '_blank');
                        }
                      }
                    }}
                    className="px-4 py-2 bg-primaryColor/20 text-primaryColor rounded-lg hover:bg-primaryColor/30 transition-colors duration-200"
                  >
                    Reabrir
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowAuthModal(false);
                      setAuthCode('');
                      setCalendarStatus(prev => ({
                        ...prev,
                        isAuthenticating: false,
                        authUrl: null
                      }));
                    }}
                    disabled={isLoading}
                    className="px-4 py-2 bg-grayLight text-white rounded-lg hover:bg-grayMedium transition-colors duration-200 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                                  <RefreshCw className="w-8 h-8 animate-spin text-emeraldMedium mx-auto mb-4" />
                <p className="text-graySubtle text-sm">Gerando URL de autenticação...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Token Modal */}
      {showTokenModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dashboardCard rounded-xl p-6 max-w-lg w-full mx-4 border border-primaryColor/20">
            <h3 className="text-white font-semibold text-lg mb-4">Conectar com Tokens</h3>

            <div className="space-y-4">
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-100">
                    <p className="font-medium mb-1">Como obter os tokens:</p>
                    <ol className="text-xs space-y-1 text-blue-200/80 list-decimal list-inside">
                      <li>Clique em "Abrir Autorização" abaixo</li>
                      <li>Autorize o aplicativo no Google</li>
                      <li>Copie os tokens da página de resposta</li>
                      <li>Cole os tokens nos campos abaixo</li>
                    </ol>
                  </div>
                </div>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  openTokenAuthUrl();
                }}
                                    className="relative w-full flex items-center justify-center gap-2 px-4 py-3 bg-grayBase/80 text-emeraldMedium rounded-lg font-medium border border-emeraldMedium shadow-sm animate-pulse-subtle mb-4 hover:bg-grayContent/90 transition-all duration-300"
              >
                  <div className="absolute inset-0 bg-grayBase/60 rounded-lg animate-pulse" style={{animationDuration: '3s'}}></div>
                  <ExternalLink className="relative z-10 w-4 h-4 text-emeraldMedium" />
                  <span className="relative z-10 text-emeraldMedium">Abrir Autorização Google</span>
              </button>

              <div>
                                  <label className="block text-sm font-medium text-graySubtle mb-2">
                                      Access Token: <span className="text-statusError">*</span>
                </label>
                <textarea
                  value={tokens.access_token}
                  onChange={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setTokens({...tokens, access_token: e.target.value});
                  }}
                  placeholder="Cole o access_token aqui..."
                  className="w-full p-3 bg-dashboardBg border border-primaryColor/30 rounded-lg text-white text-xs font-mono"
                  rows={4}
                  disabled={isLoading}
                />
              </div>

                              <div>
                  <label className="block text-sm font-medium text-graySubtle mb-2">
                    Refresh Token: <span className="text-grayLight">(opcional)</span>
                  </label>
                <textarea
                  value={tokens.refresh_token}
                  onChange={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setTokens({...tokens, refresh_token: e.target.value});
                  }}
                  placeholder="Cole o refresh_token aqui..."
                  className="w-full p-3 bg-dashboardBg border border-primaryColor/30 rounded-lg text-white text-xs font-mono"
                  rows={4}
                  disabled={isLoading}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleTokensDirectly();
                  }}
                  disabled={isLoading || !tokens.access_token.trim()}
                  className="flex-1 bg-gradient-to-r from-emeraldBase to-cyanPrimary text-white rounded-lg py-2 font-medium hover:from-emeraldBase/80 hover:to-cyanPrimary/80 transition-all duration-200 disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Conectando...
                    </div>
                  ) : (
                    'Conectar'
                  )}
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowTokenModal(false);
                    setTokens({ access_token: '', refresh_token: '' });
                  }}
                  disabled={isLoading}
                                      className="px-6 py-2 bg-grayLight text-white rounded-lg hover:bg-grayMedium transition-colors duration-200 disabled:opacity-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(GoogleCalendarSection, (prevProps, nextProps) => {
  // Comparação personalizada para evitar re-renders desnecessários
  return (
    prevProps.instanceId === nextProps.instanceId &&
    JSON.stringify(prevProps.settings) === JSON.stringify(nextProps.settings) &&
    prevProps.onSettingsChange === nextProps.onSettingsChange
  );
});
